import { z } from 'zod';

import { UuidSchema } from '../../common/id/id';
import { LocaleSchema } from '../../common/locale/locale';
import { NotificationCategorySchema } from '../notification-category/notification-category';
import { NotificationTemplateKeySchema } from '../notification-template-key/notification-template-key';

/**
 * One notification as the member reads it.
 *
 * **The copy is stored, not re-rendered.** `title` and `body` are the words that
 * were sent, in the locale they were sent in — because a template is operator
 * editable, and re-rendering an old notice through today's copy would rewrite
 * what a member was told about something that already happened. The `templateKey`
 * and `params` travel with it so a staff member reading the delivery log can see
 * what produced it.
 *
 * `occurredAt` is when the thing happened; `createdAt` is when the row was
 * written. A queued pipeline delivers late by design, and the two are different
 * moments — `NotificationRequestedEvent`'s docblock makes the same point.
 */
export const NotificationSchema = z
  .object({
    id: UuidSchema,
    category: NotificationCategorySchema,
    templateKey: NotificationTemplateKeySchema,
    locale: LocaleSchema,
    title: z.string(),
    body: z.string(),
    /** The in-app destination, already interpolated. `null` opens nothing. */
    deepLink: z.string().nullable(),
    occurredAt: z.iso.datetime(),
    readAt: z.iso.datetime().nullable(),
    createdAt: z.iso.datetime(),
  })
  .strict();

export type Notification = z.infer<typeof NotificationSchema>;

/** The badge. Its own endpoint, because the tab bar needs it without a page of rows. */
export const UnreadNotificationCountSchema = z.object({ unread: z.number().int().min(0) }).strict();

export type UnreadNotificationCount = z.infer<typeof UnreadNotificationCountSchema>;

/** How many rows one "mark all read" actually changed — the client patches its badge from it. */
export const NotificationsReadResultSchema = z.object({ marked: z.number().int().min(0) }).strict();

export type NotificationsReadResult = z.infer<typeof NotificationsReadResultSchema>;
